import { useState, useEffect, Fragment } from 'react'
import { ListItemSecondaryAction, Switch } from '@mui/material'
import { Lock } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useMutation, useQuery } from '@apollo/client'

import {
  Box,
  Container,
  ContentBox,
  CopyButton,
  List,
  ListItem,
  ListItemText,
  Row,
  Stack,
  Text,
  Button,
  useAlert,
} from '../../../components'
import { mutations, queries } from '../../../graphql'

// Global default methods and order, might move to constants file
const GLOBAL_DEFAULT_METHODS = [
  { value: 'credit-card', label: 'Credit Card' },
  { value: 'bank-transfer', label: 'Bank Transfer' },
  { value: 'securities', label: 'Securities' },
  { value: 'daf-transfer', label: 'DAF Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'crypto-currency', label: 'Crypto Currency' },
  { value: 'flow-through-shares', label: 'Flow Through Shares' },
  { value: 'shares-of-private-corporations', label: 'Shares of Private Corporations' },
  { value: 'insurance-gifts', label: 'Insurance Gifts' },
  { value: 'estate-gifts', label: 'Estate Gifts' },
]

const createMethodsState = (charityOverrides = {}) => {
  const { disabledMethods = [], customOrder = {} } = charityOverrides

  return GLOBAL_DEFAULT_METHODS.map((method) => ({
    ...method,
    active: !disabledMethods.includes(method.value),
  })).sort((a, b) => {
    const orderA = customOrder[a.value] ?? GLOBAL_DEFAULT_METHODS.findIndex((m) => m.value === a.value)
    const orderB = customOrder[b.value] ?? GLOBAL_DEFAULT_METHODS.findIndex((m) => m.value === b.value)

    if (orderA !== orderB) return orderA - orderB

    const globalIndexA = GLOBAL_DEFAULT_METHODS.findIndex((m) => m.value === a.value)
    const globalIndexB = GLOBAL_DEFAULT_METHODS.findIndex((m) => m.value === b.value)

    return globalIndexA - globalIndexB
  })
}

function SortableListItem({ method, toggleMethod, disabled }) {
  const { setNodeRef, transform, transition } = useSortable({ id: method.value })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <ListItem ref={setNodeRef} style={style}>
      <ListItemText primary={method.label} />
      <ListItemSecondaryAction>
        <Switch edge="end" onChange={() => toggleMethod(method.value)} checked={method.active} disabled={disabled} />
      </ListItemSecondaryAction>
    </ListItem>
  )
}

export function GiveGeniusResources() {
  const { id: charityId } = useParams()

  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  // Fetch charity data to check GiveGenius enabled status
  const { data: { charity } = {}, loading: charityLoading } = useQuery(queries.charities.myCharityActivity, {
    variables: { charityId },
  })

  const giveGeniusEnabled = charity?.giveGeniusEnabled === true

  const [updateCharityPaymentMethodSettings] = useMutation(mutations.charities.updateCharityPaymentMethodSettings, {
    refetchQueries: [{ query: queries.charities.getCharityPaymentMethodSettings, variables: { charityId } }],
  })

  const { data: { getCharityPaymentMethodSettings: charityOverrides } = {} } = useQuery(
    queries.charities.getCharityPaymentMethodSettings,
    {
      variables: { charityId },
      skip: !giveGeniusEnabled,
    }
  )

  const [methods, setMethods] = useState(() => createMethodsState(charityOverrides))
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (charityOverrides) {
      setMethods(createMethodsState(charityOverrides))
    }
  }, [charityOverrides])

  const toggleMethod = (value) => {
    setMethods((prev) => prev.map((m) => (m.value === value ? { ...m, active: !m.active } : m)))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      const disabledMethods = methods.filter((method) => !method.active).map((method) => method.value)

      const customOrder = {}
      methods.forEach((method, index) => {
        const defaultIndex = GLOBAL_DEFAULT_METHODS.findIndex((m) => m.value === method.value)
        if (index !== defaultIndex) {
          customOrder[method.value] = index
        }
      })

      const overrides = {}

      if (disabledMethods.length > 0) {
        overrides.disabledMethods = disabledMethods
      }
      if (Object.keys(customOrder).length > 0) {
        overrides.customOrder = customOrder
      }

      const firstActive = methods.find((m) => m.active)
      if (firstActive) {
        overrides.defaultMethod = firstActive.value
      }

      await updateCharityPaymentMethodSettings({
        variables: {
          charityId,
          disabledMethods: overrides.disabledMethods,
          customOrder: overrides.customOrder,
          defaultMethod: overrides.defaultMethod,
        },
      })

      setHasChanges(false)
      setAlert({ message: 'Payment Methods Updated', severity: 'success' })
    } catch (error) {
      setAlert({ message: 'Payment Methods Update Failed', severity: 'error' })
    }
  }

  if (charityLoading) {
    return (
      <Container maxWidth="lg">
        <Text.Body>Loading...</Text.Body>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      <Stack spacing={4}>
        <Row style={{ margin: 0 }} justifyContent="space-between">
          <Box>
            <Text.H1 sx={{ marginBottom: 2 }}>GiveGenius Resources</Text.H1>
            <Text.Body sx={{ marginBottom: 2 }}>
              GiveGenius brings all your donation options into one simple, customizable tool—so donors can give the way
              they want, and your charity gets a real-time view of their gifts through your Charity Portal dashboard.
            </Text.Body>
            <Text.Body sx={{ marginBottom: 2 }}>
              Unlock bigger gifts by making generosity easier, smarter, and more accessible.
            </Text.Body>
          </Box>
        </Row>
      </Stack>

      {!giveGeniusEnabled && (
        <ContentBox
          style={{ marginTop: 25, display: 'flex', alignItems: 'center', gap: 16 }}
          border
          sx={{ backgroundColor: 'grey.100' }}
        >
          <Lock sx={{ fontSize: 32, color: 'grey.500' }} />
          <Box>
            <Text.Bold>GiveGenius is Not Yet Enabled</Text.Bold>
            <Text.Body>
              To unlock these features, please contact GiveWise at{' '}
              <a href="mailto:charitysupport@givewise.ca" style={{ color: 'inherit' }}>
                charitysupport@givewise.ca
              </a>
            </Text.Body>
          </Box>
        </ContentBox>
      )}

      <ContentBox style={{ marginTop: 25, opacity: giveGeniusEnabled ? 1 : 0.6 }} border>
        <Text.H3 sx={{ marginBottom: 3 }}>GiveGenius URL</Text.H3>
        <Text.Body>
          This URL can be shared and will direct your donors to your GiveGenius Giving Page and guide them through
          seamless online donations right to your charity. This can be included as a hyperlink on your website or in a
          newsletter, and can also be used to create a QR code.
        </Text.Body>
        {giveGeniusEnabled ? (
          <>
            <Text.CodeBlock language="plaintext">{`${process.env.REACT_APP_SITE_URL}/gift/charity/${charityId}`}</Text.CodeBlock>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CopyButton text={`${process.env.REACT_APP_SITE_URL}/gift/charity/${charityId}`} />
            </Box>
          </>
        ) : (
          <Text.CodeBlock language="plaintext" sx={{ color: 'grey.500', userSelect: 'none' }}>
            URL will be available when GiveGenius is enabled
          </Text.CodeBlock>
        )}
      </ContentBox>

      <ContentBox style={{ marginTop: 25, opacity: giveGeniusEnabled ? 1 : 0.6 }} border>
        <Text.H3 sx={{ marginBottom: 3 }}>Customize Your Giving Options</Text.H3>
        <Text.Body sx={{ marginBottom: 2 }}>Toggle switches to enable/disable options.</Text.Body>

        <Text.Bold>Give Now</Text.Bold>
        <List>
          {methods.map((method) => {
            const isSubheaderPoint = method.value === 'flow-through-shares'
            return (
              <Fragment key={method.value}>
                {isSubheaderPoint && <Text.Bold sx={{ mt: 1 }}>Plan a Future Gift</Text.Bold>}
                <SortableListItem method={method} toggleMethod={toggleMethod} disabled={!giveGeniusEnabled} />
              </Fragment>
            )
          })}
        </List>

        <Button
          sx={{ marginBottom: 2 }}
          variant="contained"
          onClick={handleSave}
          disabled={!giveGeniusEnabled || !hasChanges}
        >
          Save Changes
        </Button>
        <Alert {...alertProps} />
      </ContentBox>

      <ContentBox style={{ marginTop: 25, opacity: giveGeniusEnabled ? 1 : 0.6 }} border>
        <Text.H3 sx={{ marginBottom: 3 }}>Helpful Resources & Support</Text.H3>
        <Text.Body sx={{ marginBottom: 2 }}>
          Below you will find a collection of resources that will provide helpful information for the GiveGenius program
          and charities. These links will open in a new tab.
        </Text.Body>

        <Stack spacing={2}>
          <ResourceLink
            title="GiveGenius Overview"
            description="Discover how GiveGenius simplifies giving and helps your team do more with less admin."
            href="https://guide.givewise.ca/for-charities/givegenius-introduction"
            disabled={!giveGeniusEnabled}
          />

          <ResourceLink
            title="Adding GiveWise to Your Donation Page"
            description="A simple guide to help you add GiveWise to your donation page. Whether you use it for all donations or just for strategic ones, we've included sample language to make it easy for donors to give."
            href="https://guide.givewise.ca/for-charities/resources-givegenius/adding-givewise-to-your-donation-page"
            disabled={!giveGeniusEnabled}
          />

          <ResourceLink
            title="How to Access Tax Receipts: Instructions for Your Donors"
            description="A ready-to-send message for donors looking for their tax receipts"
            href="https://guide.givewise.ca/for-charities/resources-givegenius/donor-instructions-how-to-access-your-tax-receipts-via-givewise"
            disabled={!giveGeniusEnabled}
          />

          <ResourceLink
            title="FAQs for Charities"
            description="Quick answers to your most common questions about using GiveWise."
            href="https://guide.givewise.ca/for-charities/givegenius-introduction/faqs"
            disabled={!giveGeniusEnabled}
          />

          <ResourceLink
            title="Quick Start Checklist"
            description="A simple checklist to help you launch and share your GiveWise giving tools."
            href="https://guide.givewise.ca/for-charities/resources-givegenius/givegenius-quick-start-checklist"
            disabled={!giveGeniusEnabled}
          />
        </Stack>
      </ContentBox>
    </Container>
  )
}

function ResourceLink({ title, description, href, disabled }) {
  return (
    <Box>
      <Text.H7>
        {disabled ? (
          <span style={{ textDecoration: 'none', color: 'inherit', cursor: 'not-allowed' }}>{title}</span>
        ) : (
          <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            {title}
          </a>
        )}
      </Text.H7>
      <Text.Body>{description}</Text.Body>
    </Box>
  )
}
